/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { useServiceContext } from '../../root'
import Events from '../../lib/Events'

const persistenceKey = 'filterTrigger'

interface FilterTriggerProps extends React.HTMLAttributes<HTMLAnchorElement> {
  noPersist?: boolean
}

const FilterTrigger: React.FC<FilterTriggerProps> = ({ noPersist, ...props }) => {
  const context = useServiceContext()
  const { bus, store, service } = context

  // Get persisted settings or default - default should be true (showing filter content)
  const settings = service?.persist ? service.persist.getGlobal<{ collapsed: boolean }>(persistenceKey) : null
  const initialSettings = settings ? settings : { collapsed: true }

  const [isCollapsed, setIsCollapsed] = useState<boolean>(initialSettings.collapsed ?? true)

  useEffect(() => {
    // Dispatch initial event and update store on mount
    if (bus) {
      const event = Events.FILTER_TOGGLE({ collapsed: isCollapsed })
      bus.dispatchEvent(new CustomEvent(event.type, { detail: event.payload }))
    }
    if (store) {
      store.filterCollapsed = isCollapsed
    }
  }, []) // Only run on mount

  const handleClick = () => {
    const newCollapsedState = !isCollapsed

    setIsCollapsed(newCollapsedState)

    // Dispatch event
    if (bus) {
      const event = Events.FILTER_TOGGLE({ collapsed: newCollapsedState })
      bus.dispatchEvent(new CustomEvent(event.type, { detail: event.payload }))
    }

    // Update store
    if (store) {
      store.filterCollapsed = newCollapsedState
    }

    // Persist if not disabled
    if (service?.persist && !noPersist) {
      service.persist.setGlobal(persistenceKey, { collapsed: newCollapsedState })
    }
  }

  return (
    <a
      {...props}
      className="btn-link filterby-expander filterby-expander-react"
      id="main-filter-btn"
      onClick={handleClick}
    >
      <span>{service?.filter?.translate('FILTERS_FILTER_BY') || 'FILTERS_FILTER_BY'}</span>
      <i
        className={'filterby-expander-caret-react fa ' + (isCollapsed ? 'fa-caret-up fa-lg' : 'fa-caret-down fa-lg')}
      />
    </a>
  )
}

export default FilterTrigger
